import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentListIcon, MagnifyingGlassIcon, ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';

interface AuditLog {
  id: number;
  actor_id: number;
  actor_name?: string;
  actor_role: string;
  action: string;
  module: string;
  entity_type?: string;
  entity_id?: string;
  old_values?: any;
  new_values?: any;
  ip_address?: string;
  http_method?: string;
  http_path?: string;
  http_status?: number;
  created_at: string;
}

const ACTION_COLORS: Record<string, string> = {
  DELETE: 'bg-red-100 text-red-700',
  SUSPEND: 'bg-orange-100 text-orange-700',
  CREATE: 'bg-green-100 text-green-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  VIEW: 'bg-gray-100 text-gray-600',
  LOGIN: 'bg-indigo-100 text-indigo-700',
  WEBHOOK: 'bg-purple-100 text-purple-700',
};

const getActionColor = (action: string) => {
  const key = Object.keys(ACTION_COLORS).find(k => action.includes(k));
  return key ? ACTION_COLORS[key] : 'bg-gray-100 text-gray-600';
};

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const limit = 50;
  const token = localStorage.getItem('aaywa_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('action', search);
    if (moduleFilter) params.set('module', moduleFilter);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);

    const res = await fetch(`${API_URL}/api/super-admin/audit-logs?${params}`, { headers });
    if (res.ok) {
      const data = await res.json();
      setLogs(data.logs);
      setTotal(data.total);
    }
    setLoading(false);
  }, [page, search, moduleFilter, fromDate, toDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCSV = () => {
    const csvHeaders = ['ID', 'Actor', 'Role', 'Action', 'Module', 'Entity', 'IP', 'Status', 'Date'];
    const rows = logs.map(l => [
      l.id, l.actor_name || l.actor_id, l.actor_role, l.action,
      l.module, `${l.entity_type || ''}:${l.entity_id || ''}`,
      l.ip_address || '', l.http_status || '', new Date(l.created_at).toISOString(),
    ]);
    const csv = [csvHeaders, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-logs-${Date.now()}.csv`; a.click();
  };

  const totalPages = Math.ceil(total / limit);
  const formatDate = (d: string) => new Date(d).toLocaleString();

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-slate-100 p-3 rounded-xl">
            <ClipboardDocumentListIcon className="w-8 h-8 text-slate-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-500 mt-0.5">Track all sensitive actions across the platform</p>
          </div>
        </div>
        <button onClick={exportCSV}
          className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-800">
          <ArrowDownTrayIcon className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search action..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-slate-400" />
          </div>
          <input type="text" placeholder="Module filter..." value={moduleFilter}
            onChange={(e) => { setModuleFilter(e.target.value); setPage(1); }}
            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-slate-400" />
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-slate-400" />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-slate-400" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
          <h2 className="font-bold text-gray-900">Events ({total})</h2>
          <button onClick={fetchLogs} className="text-gray-500 hover:text-gray-700">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-slate-500 mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Actor', 'Action', 'Module', 'Entity', 'IP', 'Status', 'Date', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-gray-900">{log.actor_name || `User #${log.actor_id}`}</div>
                        <div className="text-xs text-gray-500">{log.actor_role}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-lg text-xs font-bold ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.module}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {log.entity_type && <span>{log.entity_type}:{log.entity_id}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{log.ip_address || '—'}</td>
                      <td className="px-4 py-3">
                        {log.http_status && (
                          <span className={`px-2 py-1 rounded text-xs font-bold ${log.http_status < 400 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {log.http_status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{expanded === log.id ? '▲' : '▼'}</td>
                    </tr>
                    {expanded === log.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            {log.old_values && (
                              <div>
                                <p className="font-bold text-gray-700 mb-1">Old Values</p>
                                <pre className="bg-white p-3 rounded-lg border text-gray-600 overflow-auto max-h-32">
                                  {JSON.stringify(log.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.new_values && (
                              <div>
                                <p className="font-bold text-gray-700 mb-1">New Values</p>
                                <pre className="bg-white p-3 rounded-lg border text-gray-600 overflow-auto max-h-32">
                                  {JSON.stringify(log.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.http_path && (
                              <div className="md:col-span-2">
                                <p className="font-bold text-gray-700 mb-1">Request</p>
                                <p className="text-gray-600">{log.http_method} {log.http_path}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && (
              <div className="p-12 text-center text-gray-500">No audit logs found</div>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">Previous</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogsPage;
